'use client'

import { Package } from 'lucide-react'
import { useAffiliateImpression } from '@/hooks/use-affiliate-impression'
import type { ComparisonSeedPicks } from '@/lib/grass-comparison/product-picks'
import { cn } from '@/lib/utils'
import { ProductPickCard } from '@/components/tools/shared/product-pick-card'

interface ComparisonSeedPanelProps {
  seedPicks: ComparisonSeedPicks
}

const CTA_GRASS_LABELS: Record<string, string> = {
  'kentucky bluegrass': 'KBG',
  'tall fescue': 'Tall fescue',
  'fine fescue': 'Fine fescue',
  'perennial ryegrass': 'Ryegrass',
  bermudagrass: 'Bermuda',
  zoysiagrass: 'Zoysia',
  centipede: 'Centipede',
  bahiagrass: 'Bahia',
  buffalograss: 'Buffalo',
}

function normalizeGrassLabel(grass: string): string {
  return grass.toLowerCase().replace(/[^a-z]+/g, ' ').replace(/\s+/g, ' ').trim()
}

function seedCtaLabel(grass: string): string {
  return `Get ${CTA_GRASS_LABELS[normalizeGrassLabel(grass)] ?? grass} seed`
}

function skippedGrassNote(skippedGrasses: string[]): string | null {
  if (skippedGrasses.length === 0) return null
  const subject = skippedGrasses.join(' and ')
  const verb = skippedGrasses.length > 1 ? 'grow' : 'grows'
  const pronoun = skippedGrasses.length > 1 ? "they aren't" : "it isn't"
  return `${subject} ${verb} from sod or plugs, not seed, so ${pronoun} shown here.`
}

export function ComparisonSeedPanel({ seedPicks }: ComparisonSeedPanelProps) {
  const { columns, starter, skippedGrasses } = seedPicks
  const championGrass = columns.find((column) => column.isChampion)?.grass ?? null
  const sectionRef = useAffiliateImpression('grass-comparison-seed', {
    count: columns.length,
    champion: championGrass ?? undefined,
  })
  const note = skippedGrassNote(skippedGrasses)

  if (columns.length === 0) return null

  return (
    <section ref={sectionRef} aria-label="Seed recommendations" className="rounded-2xl border border-gray-200 bg-white p-4 shadow-elevation-2 md:p-5">
      <div className="mb-3">
        <h3 className="inline-flex items-center gap-2 text-sm font-bold tracking-tight text-gray-900">
          <Package className="h-4 w-4 text-green-600" aria-hidden />
          The seed to buy
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          {skippedGrasses.length > 0
            ? 'Recommended seed for each seedable grass in this comparison.'
            : 'Recommended seed for each grass in this comparison.'}
        </p>
      </div>

      <ul
        className={cn(
          'grid grid-cols-1 gap-3',
          columns.length === 2 && 'min-[720px]:grid-cols-2',
          columns.length >= 3 && 'min-[720px]:grid-cols-3',
        )}
        aria-label="Recommended grass seed"
      >
        {columns.map((column, index) => (
          <li key={column.pick.asin ?? column.pick.name} className="list-none">
            <h4 className="mb-1.5 flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900">
              For {column.grass}
              {column.isChampion && (
                <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                  Our pick for your yard
                </span>
              )}
            </h4>
            <ProductPickCard
              product={column.pick}
              pickIndex={index}
              variant={column.isChampion ? 'primary' : 'alternative'}
              trackingSlug="tool:grass-comparison"
              placement="tool-grass-comparison-picks"
              sourceContext="grass-comparison-seed"
              ctaLabel={seedCtaLabel(column.grass)}
              imageSize="large"
            />
          </li>
        ))}
      </ul>

      {starter && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <h4 className="mb-1.5 text-sm font-semibold text-gray-900">
            Starter companion
          </h4>
          <ProductPickCard
            product={starter}
            pickIndex={columns.length}
            variant="shopping-list"
            trackingSlug="tool:grass-comparison"
            placement="tool-grass-comparison-picks"
            sourceContext="grass-comparison-seed"
            ctaLabel="Get starter"
          />
        </div>
      )}

      {note && <p className="mt-3 text-xs text-gray-500">{note}</p>}

      <p className="mt-3 text-center text-xs text-gray-400">
        As an Amazon Associate, we earn from qualifying purchases.
      </p>
    </section>
  )
}
